import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { api } from "./api";

const setNoindex = (on) => {
  let m = document.querySelector('meta[name="robots"][data-beta="1"]');
  if (on && !m) {
    m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex,nofollow";
    m.dataset.beta = "1";
    document.head.appendChild(m);
  }
  if (!on && m) m.remove();
};

export default function BetaGate({ children }) {
  const [state, setState] = useState("checking");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let dead = false;
    const probe = (attempt) => api.get("/beta/state")
      .then((r) => {
        if (dead) return;
        if (!r.data.gated || r.data.authorized) setState("open");
        else { localStorage.removeItem("xp_beta_token"); setState("locked"); }
      })
      .catch(() => {
        if (dead) return;
        if (attempt < 1) setTimeout(() => probe(attempt + 1), 1200);
        else setState("locked"); // fail closed — the server-side wall blocks data either way
      });
    probe(0);
    const onLocked = () => setState("locked");
    window.addEventListener("xp-beta-locked", onLocked);
    return () => { dead = true; window.removeEventListener("xp-beta-locked", onLocked); };
  }, []);

  useEffect(() => {
    setNoindex(state === "locked");
    return () => setNoindex(false);
  }, [state]);

  if (window.location.pathname.startsWith("/staff")) return children; // staff tablets have their own PIN wall
  if (window.location.pathname.startsWith("/gold")) return children; // public giveaway pages, no login ever
  if (state === "open") return children;
  if (state === "checking") return <div data-testid="beta-gate-checking" className="min-h-screen bg-[#0A0A0A]" />;

  const unlock = async () => {
    if (!code.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post("/beta/unlock", { code: code.trim() });
      localStorage.setItem("xp_beta_token", r.data.token);
      setCode("");
      setState("open");
    } catch (e) {
      setError(e.response?.status === 429
        ? "Too many attempts — please wait a few minutes"
        : "That access code is not valid");
    }
    setBusy(false);
  };

  return (
    <div data-testid="beta-gate-screen" className="min-h-screen flex items-center justify-center px-6"
      style={{ background: "radial-gradient(ellipse at 50% 30%, #16120A 0%, #0A0A0A 60%)" }}>
      <div className="w-full max-w-sm text-center">
        <img src="/urban-emblem.png" alt="Urban by VeerGi" className="h-16 mx-auto" />
        <p className="text-[10px] tracking-[0.5em] text-[#D4AF37] font-bold mt-8">PRIVATE BETA</p>
        <h1 data-testid="beta-gate-title" className="font-display text-3xl font-bold text-white mt-2">Authorized testing only</h1>
        <p className="text-xs text-white/45 mt-3 leading-relaxed">
          Urban Passport is being polished behind closed doors. If you're part of the tasting crew, enter your access code below.
        </p>
        <input
          data-testid="beta-code-input"
          type="text"
          value={code}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          onChange={(e) => { setCode(e.target.value); setError(""); }}
          onKeyDown={(e) => e.key === "Enter" && unlock()}
          placeholder="Access code"
          className="mt-8 w-full bg-[#141414] border border-[#D4AF37]/25 rounded-full px-5 py-3.5 text-center text-sm text-white tracking-widest focus:outline-none focus:border-[#D4AF37]/70"
        />
        {error && <p data-testid="beta-error" className="text-xs text-[#E74C3C] mt-3">{error}</p>}
        <button
          data-testid="beta-unlock-btn"
          onClick={unlock}
          disabled={busy || !code.trim()}
          className="mt-4 w-full bg-[#D4AF37] text-black font-bold rounded-full py-3.5 text-xs tracking-[0.25em] hover:bg-[#F1C40F] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Lock size={13} /> {busy ? "CHECKING…" : "UNLOCK PREVIEW"}
        </button>
        <p className="text-[9px] text-white/25 mt-6 tracking-widest">URBAN BY VEERGI · MYURBANPASSPORT.COM</p>
      </div>
    </div>
  );
}
